import { supabase } from '../utils/supabase';
import { Atendimento } from '../types';

export interface CreateAtendimentoData {
  paciente_id: string;
  is_priority?: boolean;
}

export interface AtendimentoServiceResponse {
  success: boolean;
  message: string;
  data?: Atendimento;
  error?: Error;
}

export interface AtendimentosListResponse {
  success: boolean;
  message: string;
  data?: Atendimento[];
  error?: Error;
}

export class AtendimentoService {
  /**
   * Add a patient to the attendance queue
   */
  static async addToQueue(
    organizationId: string,
    data: CreateAtendimentoData
  ): Promise<AtendimentoServiceResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    if (!data.paciente_id) {
      return {
        success: false,
        message: 'Patient ID is required',
        error: new Error('Patient ID is required')
      };
    }

    try {
      const { data: atendimento, error } = await supabase
        .from('atendimentos')
        .insert({
          organization_id: organizationId,
          paciente_id: data.paciente_id,
          is_priority: data.is_priority || false
        })
        .select(`
          *,
          paciente:pacientes(*)
        `)
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Paciente adicionado à fila com sucesso',
        data: atendimento
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Get current queue for an organization
   */
  static async getQueue(organizationId: string): Promise<AtendimentosListResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      const { data: atendimentos, error } = await supabase
        .from('atendimentos')
        .select(`
          *,
          paciente:pacientes(*)
        `)
        .eq('organization_id', organizationId)
        .is('finished_at', null)
        .is('canceled_at', null)
        .order('is_priority', { ascending: false })
        .order('joined_at', { ascending: true });

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Fila carregada com sucesso',
        data: atendimentos || []
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Call a patient for consultation
   */
  static async callPatient(atendimentoId: string): Promise<AtendimentoServiceResponse> {
    if (!atendimentoId) {
      return {
        success: false,
        message: 'Atendimento ID is required',
        error: new Error('Atendimento ID is required')
      };
    }

    try {
      const { data: atendimento, error } = await supabase
        .from('atendimentos')
        .update({
          called_at: new Date().toISOString()
        })
        .eq('id', atendimentoId)
        .select(`
          *,
          paciente:pacientes(*)
        `)
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Paciente chamado com sucesso',
        data: atendimento
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Finish attendance with a patient
   */
  static async finishAttendance(atendimentoId: string): Promise<AtendimentoServiceResponse> {
    if (!atendimentoId) {
      return {
        success: false,
        message: 'Atendimento ID is required',
        error: new Error('Atendimento ID is required')
      };
    }

    try {
      const { data: atendimento, error } = await supabase
        .from('atendimentos')
        .update({
          finished_at: new Date().toISOString()
        })
        .eq('id', atendimentoId)
        .select(`
          *,
          paciente:pacientes(*)
        `)
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Atendimento finalizado com sucesso',
        data: atendimento
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Cancel/remove a patient from the queue
   */
  static async cancelAttendance(atendimentoId: string): Promise<AtendimentoServiceResponse> {
    if (!atendimentoId) {
      return {
        success: false,
        message: 'Atendimento ID is required',
        error: new Error('Atendimento ID is required')
      };
    }

    try {
      const { data: atendimento, error } = await supabase
        .from('atendimentos')
        .update({
          canceled_at: new Date().toISOString()
        })
        .eq('id', atendimentoId)
        .select(`
          *,
          paciente:pacientes(*)
        `)
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Atendimento cancelado com sucesso',
        data: atendimento
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Get currently called patient (for monitor screen)
   */
  static async getCurrentlyCalled(organizationId: string): Promise<AtendimentoServiceResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      const { data: atendimento, error } = await supabase
        .from('atendimentos')
        .select(`
          *,
          paciente:pacientes(*)
        `)
        .eq('organization_id', organizationId)
        .not('called_at', 'is', null)
        .is('finished_at', null)
        .is('canceled_at', null)
        .order('called_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Paciente atual carregado com sucesso',
        data: atendimento || undefined
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Get last called patients (for monitor screen)
   */
  static async getLastCalled(organizationId: string, limit: number = 3): Promise<AtendimentosListResponse> {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization ID is required',
        error: new Error('Organization ID is required')
      };
    }

    try {
      const { data: atendimentos, error } = await supabase
        .from('atendimentos')
        .select(`
          *,
          paciente:pacientes(*)
        `)
        .eq('organization_id', organizationId)
        .not('called_at', 'is', null)
        .not('finished_at', 'is', null)
        .order('finished_at', { ascending: false })
        .limit(limit);

      if (error) {
        throw new Error(error.message);
      }

      return {
        success: true,
        message: 'Últimos pacientes chamados carregados com sucesso',
        data: atendimentos || []
      };
    } catch (error) {
      return {
        success: false,
        message: (error as Error).message,
        error: error as Error
      };
    }
  }

  /**
   * Subscribe to real-time changes in the attendance queue
   */
  static subscribeToQueue(organizationId: string, callback: (payload: any) => void) {
    return supabase
      .channel(`atendimentos-${organizationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'atendimentos',
          filter: `organization_id=eq.${organizationId}`
        },
        callback
      )
      .subscribe();
  }
}
